/**
 * ApprovalModal — decide one approval request.
 *
 * Opened from a toast, from the Notification Centre, or from a deep link in the
 * approval email. It loads the request by id rather than being handed one, so
 * all three entry points behave identically and none of them can show a stale
 * copy of something that has since been decided.
 *
 * THE RULE THIS FOLLOWS, same as the Undo button: the UI never decides whether
 * you may approve. The server sends `canDecide` plus `cannotDecideReason` per
 * record, and the buttons are DISABLED WITH THAT REASON, never hidden. Hiding
 * them would leave an approver who followed an email link staring at a screen
 * with no explanation.
 */

import { useCallback, useEffect, useState } from 'react';
import Modal from '@/components/ui/Modal.jsx';
import * as api from '@/api/endpoints/approvals.js';
import { useToast } from '@/components/ui/useToast.js';

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

function timeLeft(iso) {
  const until = new Date(iso).getTime();
  if (Number.isNaN(until)) return null;
  const mins = Math.round((until - Date.now()) / 60000);
  if (mins <= 0) return 'expired';
  if (mins < 60) return `${mins} min left`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} hr left`;
  return `${Math.round(hours / 24)} days left`;
}

const STATUS_STYLE = {
  pending: { label: 'Waiting for a decision', bg: 'rgba(245,158,11,0.14)', fg: '#b45309' },
  approved: { label: 'Approved', bg: 'rgba(16,185,129,0.14)', fg: '#047857' },
  executed: { label: 'Approved and applied', bg: 'rgba(16,185,129,0.14)', fg: '#047857' },
  rejected: { label: 'Rejected', bg: 'rgba(239,68,68,0.14)', fg: '#b91c1c' },
  expired: { label: 'Expired — nobody decided it', bg: 'rgba(107,114,128,0.14)', fg: '#4b5563' },
  cancelled: { label: 'Withdrawn by the requester', bg: 'rgba(107,114,128,0.14)', fg: '#4b5563' },
  failed: { label: 'Approved, but the change failed', bg: 'rgba(239,68,68,0.14)', fg: '#b91c1c' }
};

function Chip({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.pending;
  return (
    <span style={{
      fontSize: 11.5, fontWeight: 600, padding: '3px 10px', borderRadius: 999,
      background: s.bg, color: s.fg, whiteSpace: 'nowrap'
    }}>{s.label}</span>
  );
}

function Row({ label, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid var(--hairline-strong)' }}>
      <div style={{ minWidth: 130, fontSize: 12.5, color: 'var(--muted-2)', fontWeight: 600 }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, color: 'var(--ink)', minWidth: 0 }}>{children}</div>
    </div>
  );
}

export default function ApprovalModal({ open, approvalId, onClose, onDecided }) {
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null); // 'approve' | 'reject' | null

  const load = useCallback(async () => {
    if (!approvalId) return;
    setLoading(true);
    setError(null);
    try {
      setRequest(await api.fetchApprovalRequest(approvalId));
    } catch (err) {
      setRequest(null);
      setError(err?.message ?? 'Could not load this request.');
    } finally {
      setLoading(false);
    }
  }, [approvalId]);

  useEffect(() => {
    if (!open) return;
    setNote('');
    void load();
  }, [open, load]);

  async function decide(verb) {
    setBusy(verb);
    try {
      const result = verb === 'approve' ? await api.approveRequest(approvalId, note) : await api.rejectRequest(approvalId, note);
      toast.success(verb === 'approve' ? 'Approved' : 'Rejected', result?.note ?? '');
      onDecided?.();
      onClose?.();
    } catch (err) {
      // Every refusal carries a specific reason from the server — show it
      // rather than a generic failure, and reload so the screen reflects
      // whatever actually happened.
      toast.error(verb === 'approve' ? 'Could not approve' : 'Could not reject', err?.message ?? 'The request could not be decided.');
      void load();
    } finally {
      setBusy(null);
    }
  }

  const pending = request?.status === 'pending';
  const blocked = request ? !request.canDecide : true;
  const disabledReason = request?.cannotDecideReason
    ?? (request && !pending ? 'This request has already been decided.' : null);

  return (
    <Modal
      open={open}
      onClose={onClose}
      id="approvalModal"
      title="Approval request"
      titleIcon="fa-user-shield"
      headerStyle={{ background: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)' }}
      width="720px"
      ariaLabel="Approval request"
      className="modal--above-admin"
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>
            {pending && request?.expiresAt ? `Expires — ${timeLeft(request.expiresAt)}` : ''}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="lg-btn lg-btn--glass" onClick={onClose}>Close</button>
            <button
              type="button"
              className="lg-btn lg-btn--glass"
              style={{ color: 'var(--bad)', opacity: blocked || !pending || busy ? 0.5 : 1 }}
              disabled={blocked || !pending || Boolean(busy)}
              title={disabledReason ?? undefined}
              onClick={() => decide('reject')}
            >
              {busy === 'reject' ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-times" />} Reject
            </button>
            <button
              type="button"
              className="lg-btn lg-btn--green"
              style={{ opacity: blocked || !pending || busy ? 0.5 : 1 }}
              disabled={blocked || !pending || Boolean(busy)}
              title={disabledReason ?? undefined}
              onClick={() => decide('approve')}
            >
              {busy === 'approve' ? <i className="fas fa-circle-notch fa-spin" /> : <i className="fas fa-check" />} Approve
            </button>
          </div>
        </div>
      }
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: 32, color: 'var(--muted-2)' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 26 }} />
          <p style={{ marginTop: 10 }}>Loading the request…</p>
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: 14, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: 'var(--bad)' }}>
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {request && !loading && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.4 }}>
              {request.targetSummary}
            </div>
            <Chip status={request.status} />
          </div>

          {/* The justification is the whole reason an approver can decide
              anything useful, so it leads rather than sitting in the table. */}
          {request.justification ? (
            <div style={{
              padding: 12, marginBottom: 14, borderRadius: 8,
              background: 'var(--glass-fill-65)', border: '1px solid var(--glass-border-bright)'
            }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', color: 'var(--faint-2)', letterSpacing: 0.5, marginBottom: 4 }}>
                Why they are asking
              </div>
              <div style={{ fontSize: 13.5, color: 'var(--ink)', fontStyle: 'italic' }}>&ldquo;{request.justification}&rdquo;</div>
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--muted-2)', marginBottom: 14 }}>No reason was given for this request.</div>
          )}

          <Row label="Requested by">
            {request.requester?.userName || request.requester?.userEmail || 'unknown'}
            <span style={{ color: 'var(--muted-2)' }}> · {relativeTime(request.createdAt)}</span>
          </Row>
          <Row label="Approver must hold">
            {request.requiredTier} access on {(request.requiredAnyOf ?? []).join(' or ') || 'the Admin section'}
          </Row>
          <Row label="Why approval is needed">
            {request.reason === 'dual-control'
              ? 'This action always needs a second pair of eyes.'
              : request.reason === 'elevation'
                ? 'It is above the requester’s own access level.'
                : request.reason === 'bulk'
                  ? 'It affects more records than one person may change unreviewed.'
                  : 'A policy on this action requires it.'}
          </Row>
          {request.decider && (
            <Row label="Decided by">
              {request.decider.userName || request.decider.userEmail}
              <span style={{ color: 'var(--muted-2)' }}> · {relativeTime(request.decidedAt)}</span>
              {request.decisionNote ? <div style={{ marginTop: 4, color: 'var(--muted-2)' }}>{request.decisionNote}</div> : null}
            </Row>
          )}
          {request.error && (
            <Row label="What went wrong">
              <span style={{ color: 'var(--bad)' }}>{request.error}</span>
            </Row>
          )}

          {/* Drift: something about the target changed between the request
              being raised and now. Not a blocker — the operation's own rules
              refuse to invent a change — but the approver should see it before
              deciding rather than after. */}
          {request.revalidation?.changed && (
            <div style={{
              marginTop: 14, padding: 12, borderRadius: 8,
              background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)'
            }}>
              <div style={{ fontWeight: 700, color: '#b45309', fontSize: 13, marginBottom: 6 }}>
                <i className="fas fa-triangle-exclamation" /> Some details changed since this was requested
              </div>
              {Object.entries(request.revalidation.changed).map(([field, v]) => (
                <div key={field} style={{ fontSize: 12.5, color: 'var(--ink)' }}>
                  <strong>{field}</strong>: was “{String(v?.was ?? '')}”, now “{String(v?.now ?? '')}”
                </div>
              ))}
            </div>
          )}

          {!request.hasPreImage && pending && (
            <div style={{ marginTop: 14, fontSize: 12.5, color: 'var(--muted-2)' }}>
              <i className="fas fa-circle-info" /> The state before this change could not be recorded when the request was
              raised, so it may not be possible to undo afterwards.
            </div>
          )}

          {blocked && disabledReason && (
            <div style={{
              marginTop: 14, padding: 12, borderRadius: 8,
              background: 'var(--glass-fill-65)', border: '1px solid var(--glass-border-bright)',
              fontSize: 12.5, color: 'var(--muted-2)'
            }}>
              <i className="fas fa-lock" /> {disabledReason}
            </div>
          )}

          {pending && !blocked && (
            <div style={{ marginTop: 16 }}>
              <label htmlFor="approvalNote" style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--muted-2)', marginBottom: 6 }}>
                Note (optional — the requester sees this)
              </label>
              <textarea
                id="approvalNote"
                className="lg-input"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Confirmed with the line manager."
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
